import { useEffect, useMemo } from 'react';
import jwtDecode from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { paths } from '@app/constants';
import { useB2BActivity } from '@app/pages/b2b/hooks';
import { getRestorablePath, showDefaultErrorToast } from '@app/utils';
import {
  APIError,
  IToken,
  Language,
  useAcceptInviteAsExistingUser,
  useAcceptMilestoneInviteAsExistingUser,
  useLogin,
  useTokenData,
} from '@data';
import { LoginBody, Role, TokenType } from '@data/sdk/match';
import { pulse } from '@data/utils/pulse';
import { AntdForm } from '@ui/components';

export type SignInFormValues = {
  email: string;
  password: string;
};

export const useSignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [form] = AntdForm.useForm<SignInFormValues>();
  const [token] = useQueryParam('token', StringParam);

  const { trackLoginSuccess } = useB2BActivity();

  const { data: tokenData } = useTokenData(token ?? '');
  const { mutate: acceptInvite } = useAcceptInviteAsExistingUser();
  const { mutate: acceptMilestoneInvite } = useAcceptMilestoneInviteAsExistingUser();
  const { mutate: login, isPending: loginWithCredentialsLoading } = useLogin();

  const initialValues: SignInFormValues = useMemo(
    () => ({
      email: tokenData?.email ?? '',
      password: '',
    }),
    [tokenData],
  );

  const loginWithCredentials = (values: SignInFormValues) => {
    login(
      { ...values, language: i18n.language as Language },
      {
        onSuccess: (res) => {
          const { user_id: responseUserId, userData } = jwtDecode<IToken>(res.token);

          if (!userData?.roles.includes(Role.Volunteer)) {
            trackLoginSuccess(responseUserId, userData?.roles[0] as Role);
          } else {
            // Patient login — the first-party funnel event (trackLoginSuccess is b2b-only).
            pulse.loginSucceeded({ method: 'email' });
          }

          if (token) {
            if (tokenData?.tokenType === TokenType.Milestone) {
              const isPatient = userData?.roles.includes(Role.Volunteer);
              if (isPatient) {
                acceptMilestoneInvite(
                  {
                    acceptMilestoneTokenExistingBody: {
                      tokenValue: token,
                    },
                  },
                  {
                    onSettled: () => {
                      window.location.replace(`/${paths.app}/${paths.trials}`);
                    },
                  },
                );
              } else {
                navigate(`/${paths.app}`);
              }
            } else if (tokenData?.tokenType !== TokenType.TeamCreate) {
              acceptInvite(
                { tokenValue: token, teamRole: tokenData?.teamRole },
                { onSuccess: () => navigate(`/${paths.app}`) },
              );
            }
          } else {
            const { from, userId } = location.state ?? {};

            // A destination the route guard interrupted wins over the role's landing page.
            const restorablePath = getRestorablePath({
              from,
              previousUserId: userId,
              currentUserId: responseUserId,
            });

            if (restorablePath) {
              window.location.replace(restorablePath);
              return;
            }

            if (userData?.roles.includes(Role.Volunteer)) {
              navigate(`/${paths.select_account}`);
            } else {
              navigate(`/${paths.app}`);
            }
          }
        },
        onError: (error: APIError<LoginBody>) => {
          if (error?.response?.status === 400) {
            showDefaultErrorToast(error);
          }
        },
      },
    );
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues]);

  return {
    form,
    initialValues,
    loginWithCredentialsLoading,
    loginWithCredentials,
  };
};
